import { useEffect } from "react";
import { ButtonGroup, Dropdown } from "react-bootstrap";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { registerObject } from "go-form";
import FileUpload from "go-app/components/ImageForm/FileUpload";
import { ReactComponent as EmptyImageSVG } from "go-component/images/svg/angle-input.svg";
import { ReactComponent as MoreSVG } from "go-core/images/svg/actions/more.svg";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { FileAttachmentApi, LoanApi } from "../../../../services/Api/types";
import { downloadFileWithAccessToken } from "../../../../utils";
import FileDisplay from "../FileUpload/FileDisplay";

interface Props {
	form: UseFormReturn<LoanApi>;
	loanStatus: string;
	loanAcceptationStatus: string;
	handleUpdate?: (attachments: any[]) => void;
}

const LoanAttachments = (props: Props) => {
	const { t } = useTranslation("admin");
	const confirmation = useConfirmation();
	const { control, register, setValue } = props.form;
	const { append, remove, fields } = useFieldArray({
		keyName: "key",
		name: "attachments",
		control,
	});

	const getBase64 = (file: any) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => reject(error);
		});
	};

	const onRemoveFile = async (index: number) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.remove", { ns: "lib" }),
			});
			if (props.handleUpdate) {
				remove(index);
				props.handleUpdate(fields.filter((f, ind) => index !== ind));
			} else {
				remove(index);
			}
		} catch (err) {
			return null;
		}
	};

	const registerFile = (file: FileAttachmentApi, index: number) => {
		registerObject(register, `attachments.${index}`, ["name", "content", "id", "url"]);
		setValue(`attachments.${index}`, file);
	};

	const onUpdateFile = async (file: File | undefined) => {
		if (!file) return;

		const fileAsBase64: any = await getBase64(file);
		const createdFile = {
			name: file.name,
			content: fileAsBase64.split(",")[1],
		};
		if (props.handleUpdate) {
			append(createdFile);
			props.handleUpdate([...fields, createdFile]);
		} else {
			append(createdFile);
		}
	};

	useEffect(() => {
		fields.forEach((field, index) => {
			registerFile(field, index);
		});
	}, [fields]);

	const isAttachmentRemovable = () => {
		return (
			props.loanStatus === "DRAFT" ||
			props.loanStatus === "OPEN" ||
			(props.loanStatus === "ACCEPTED" && props.loanAcceptationStatus !== "ACCEPTED")
		);
	};

	return (
		<div className={"loan-attachments"}>
			<h5>{t("modules.loan.field.attachments.title")}</h5>
			<div className={"d-flex flex-wrap gap-2"}>
				{fields.map((field, index) => {
					return (
						<div
							className={"file-attachment-wrapper"}
							key={fields[index].key}
							onClick={() => downloadFileWithAccessToken(field)}
						>
							<div className={"dropzone d-flex"}>
								<Dropdown onClick={(evt) => evt.stopPropagation()} as={ButtonGroup} className="actions">
									<Dropdown.Toggle draggable={false} as={MoreSVG}>
										{t("common.action.more", { ns: "lib" })}
									</Dropdown.Toggle>
									<Dropdown.Menu>
										{isAttachmentRemovable() && (
											<Dropdown.Item onClick={() => onRemoveFile(index)}>
												{t("common.action.remove", { ns: "lib" })}
											</Dropdown.Item>
										)}
										<Dropdown.Item onClick={() => downloadFileWithAccessToken(field)}>
											{t("common.action.download")}
										</Dropdown.Item>
									</Dropdown.Menu>
								</Dropdown>
								<FileDisplay fileName={field.name} icon={<EmptyImageSVG />} />
							</div>
						</div>
					);
				})}
				{props.loanStatus !== "CLOSED" && (
					<div className={"file-attachment-wrapper"}>
						<FileUpload
							customString={t("modules.loan.action.add_attachment.title")}
							onUpdateFile={(_, file) => onUpdateFile(file)}
						/>
					</div>
				)}
			</div>
		</div>
	);
};
export default LoanAttachments;
